/**
 * AuthService for SUSU Closet platform
 * Handles unified cross-platform authentication via SHEYOU
 */
class AuthService {
  constructor() {
    this.platformName = 'susu_closet';
    this.backendUrl = this.getBackendUrl();
    this.platformUrls = this.getPlatformUrls();
  }

  /**
   * Detect environment and return appropriate backend URL
   * @returns {string} Backend API URL
   */
  getBackendUrl() {
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.startsWith('172.');

    let url = isDev
      ? process.env.REACT_APP_backend_baseUrl_localTest
      : process.env.REACT_APP_backend_baseUrl_live;

    if (!url) url = 'http://localhost:1337';

    return url;
  }

  /**
   * Get platform URLs based on current environment
   * @returns {Object} Object containing URLs for all platforms
   */
  getPlatformUrls() {
    const hn = window.location.hostname;
    const isDev = hn === 'localhost' ||
      hn === '127.0.0.1' ||
      hn.startsWith('192.168.') ||
      hn.startsWith('10.') ||
      hn.startsWith('172.');

    return {
      sheyou: isDev ? 'http://localhost:3000' : 'https://sheyou.co',
      susu_closet: isDev ? 'http://localhost:3002' : 'https://susucloset.com',
      virtual_fashion: isDev ? 'http://localhost:3003' : 'https://susumap.com'
    };
  }

  /**
   * Redirect to SHEYOU for authentication
   * @param {string} returnUrl - URL to return to after authentication (optional)
   */
  redirectToSHEYOULogin(returnUrl = null) {
    const currentUrl = returnUrl || window.location.href;
    const params = new URLSearchParams();

    // Add platform identifier
    params.append('platform', this.platformName);

    // Add return URL
    params.append('return_to', currentUrl);

    const sheyouLoginUrl = `${this.platformUrls.sheyou}/login?${params.toString()}`;

    // Store original URL in sessionStorage for fallback
    sessionStorage.setItem('auth_return_url', currentUrl);
    sessionStorage.setItem('auth_platform', this.platformName);

    window.location.href = sheyouLoginUrl;
  }

  /**
   * Handle authentication return from SHEYOU with token
   * @param {string} token - JWT token from authentication
   * @returns {Promise<Object>} Authentication result
   */
  async handleAuthReturn(token) {
    try {
      if (!token) {
        throw new Error('No authentication token provided');
      }

      // Validate token with backend
      const validation = await this.validateToken(token);

      if (!validation.valid) {
        throw new Error(validation.error || 'Token validation failed');
      }

      // Store authentication data
      this.storeAuthData(token, validation.user);

      // Clear temporary storage
      sessionStorage.removeItem('auth_return_url');
      sessionStorage.removeItem('auth_platform');

      return {
        success: true,
        user: validation.user,
        token: token
      };

    } catch (error) {
      console.error('Authentication return handling failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Validate token with backend API
   * @param {string} token - JWT token to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateToken(token) {
    try {
      const response = await fetch(`${this.backendUrl}/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Validation failed: ${response.status}`);
      }

      const result = await response.json();

      // Map the backend response to match expected format
      return {
        valid: result.isValid,
        user: result.user,
        error: result.error
      };

    } catch (error) {
      console.error('Token validation failed:', error);
      return {
        valid: false,
        error: error.message
      };
    }
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile() {
    try {
      const token = this.getStoredToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${this.backendUrl}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          this.clearAuthData();
          throw new Error('Authentication expired');
        }
        throw new Error(`Profile fetch failed: ${response.status}`);
      }

      const profile = await response.json();
      return {
        success: true,
        user: profile.user
      };

    } catch (error) {
      console.error('Get user profile failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Logout user and clear authentication data
   * @returns {Promise<Object>} Logout result
   */
  async logout() {
    try {
      const token = this.getStoredToken();

      if (token) {
        // Notify backend of logout
        try {
          await fetch(`${this.backendUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (error) {
          // Continue with local logout even if backend call fails
          console.warn('Backend logout failed:', error);
        }
      }

      // Clear local authentication data
      this.clearAuthData();

      // Clear all user-specific localStorage data
      this.clearAllUserData();

      return {
        success: true
      };

    } catch (error) {
      console.error('Logout failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Clear all user-specific data from localStorage
   */
  clearAllUserData() {
    try {
      // Auth-related keys
      const authKeys = [
        'auth_token',
        'auth_user',
        'auth_timestamp',
        'susu_auth_session',
        'susu_auth_token',
        'susu_auth_user',
        'token',
        'user',
        'success',
        'login',
        'identifier',
        'password',
        'rememberMe',
        'emailConfirmed',
        'verificationCode'
      ];

      // User data keys
      const userDataKeys = [
        'guestClothes',
        'savedLooks',
        'weeklyLooks',
        'currentLook',
        'currentVirtualTryOnResults'
      ];

      // VTO and canvas state keys
      const stateKeys = [
        'vto_history',
        'vto_enhanced_state',
        'vtoUIState',
        'personImageUIState',
        'personImageState',
        'backgroundState',
        'canvasZoomState',
        'panelLayoutPreferences',
        'tryOnMode'
      ];

      // Clear all keys
      [...authKeys, ...userDataKeys, ...stateKeys].forEach(key => {
        localStorage.removeItem(key);
      });

      // Clear sessionStorage
      sessionStorage.clear();

      console.log('[Auth] All user data cleared from localStorage');
    } catch (error) {
      console.error('Failed to clear all user data:', error);
    }
  }

  /**
   * Check if user is currently authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    const token = this.getStoredToken();
    const user = this.getStoredUser();

    return !!(token && user);
  }

  /**
   * Get current authentication status
   * @returns {Object} Authentication status and user data
   */
  getAuthStatus() {
    const token = this.getStoredToken();
    const user = this.getStoredUser();

    return {
      isAuthenticated: !!(token && user),
      user: user || null,
      token: token || null
    };
  }

  /**
   * Store authentication data in localStorage
   * @param {string} token - JWT token
   * @param {Object} user - User data
   */
  storeAuthData(token, user) {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    localStorage.setItem('auth_timestamp', Date.now().toString());
  }

  /**
   * Get stored authentication token
   * @returns {string|null} Stored token or null
   */
  getStoredToken() {
    return localStorage.getItem('auth_token');
  }

  /**
   * Get stored user data
   * @returns {Object|null} Stored user data or null
   */
  getStoredUser() {
    const userData = localStorage.getItem('auth_user');
    try {
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Failed to parse stored user data:', error);
      return null;
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuthData() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_timestamp');
    sessionStorage.removeItem('auth_return_url');
    sessionStorage.removeItem('auth_platform');
  }

  /**
   * Check if authentication data is expired
   * @param {number} maxAge - Maximum age in milliseconds (default: 24 hours)
   * @returns {boolean} True if expired
   */
  isAuthDataExpired(maxAge = 24 * 60 * 60 * 1000) {
    const timestamp = localStorage.getItem('auth_timestamp');

    if (!timestamp) {
      return true;
    }

    const age = Date.now() - parseInt(timestamp);
    return age > maxAge;
  }
}

export default AuthService;